import type { Session } from './session'
import type { ActivityEntry } from './energy'

/**
 * Antrenman takvimi — saf mantık.
 *
 * Geçmiş bir liste değil bir takvim olmalı: "geçen salı ne yaptım" sorusunun
 * cevabı 15 satırlık listede aramak değil, takvimde salıya bakmak. Ayrıca
 * geriye dönük giriş de buradan doğuyor — boş bir güne dokunup doldurmak,
 * "hangi tarihti" diye düşünmekten kolay.
 *
 * Tarihler her yerde 'YYYY-MM-DD' metni. Date nesnesiyle uğraşmıyoruz:
 * saat dilimi kaydırması yüzünden gün bir eksik/fazla çıkıyor ve bu hata
 * ancak biri "dün girdiğim antrenman önceki günde görünüyor" diye şikayet
 * edince fark ediliyor.
 */

export type DayMark =
  /** Tamamlanmış kuvvet antrenmanı. */
  | 'done'
  /** Başlanmış ama bitirilmemiş. */
  | 'partial'
  /** Kuvvet yok ama aktivite (tenis, yürüyüş…) kaydı var. */
  | 'activity'
  /** Şablonda kuvvet günü ama hiç kayıt yok — geçmişte kaldıysa açık bir boşluk. */
  | 'missed'
  /** Kayıt da plan da yok. */
  | 'empty'

export interface CalendarDay {
  date: string
  /** Ayın kaçı. */
  day: number
  /** Görüntülenen ay dışındaki dolgu günleri (önceki/sonraki ay). */
  outside: boolean
  today: boolean
  future: boolean
  mark: DayMark
  /** Varsa gün etiketi: "Tüm vücut A" gibi. */
  label: string | null
  activityCount: number
}

/** 'YYYY-MM-DD' → gün ekle/çıkar. Date kullanmadan, UTC üzerinden güvenli. */
export function shiftDate(iso: string, days: number): string {
  const t = Date.parse(`${iso}T00:00:00Z`)
  return new Date(t + days * 86400000).toISOString().slice(0, 10)
}

/** Ayın ilk günü: '2026-08-14' → '2026-08-01'. */
export function monthStart(iso: string): string {
  return `${iso.slice(0, 7)}-01`
}

/** Ay kaydırma: '2026-08-01' + (-1) → '2026-07-01'. */
export function shiftMonth(iso: string, months: number): string {
  const y = Number(iso.slice(0, 4))
  const m = Number(iso.slice(5, 7)) - 1 + months
  const yy = y + Math.floor(m / 12)
  const mm = ((m % 12) + 12) % 12
  return `${yy}-${String(mm + 1).padStart(2, '0')}-01`
}

/** Pazartesi = 0. JS'in `getUTCDay()` pazarı 0 sayıyor; hafta pazartesi başlıyor. */
export function weekdayMon0(iso: string): number {
  const d = new Date(`${iso}T00:00:00Z`).getUTCDay()
  return (d + 6) % 7
}

/** Haftanın pazartesisi. */
export function weekStartMon(iso: string): string {
  return shiftDate(iso, -weekdayMon0(iso))
}

const AY = [
  'Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran',
  'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık',
]

export function monthLabel(iso: string): string {
  return `${AY[Number(iso.slice(5, 7)) - 1]} ${iso.slice(0, 4)}`
}

export const GUN_KISA = ['Pt', 'Sa', 'Ça', 'Pe', 'Cu', 'Ct', 'Pz']

interface BuildInput {
  /** Görüntülenecek ayın herhangi bir günü (ay görünümü) ya da haftanın günü. */
  anchor: string
  today: string
  sessions: Session[]
  activities: ActivityEntry[]
  /** Şablonda kuvvet olan hafta günleri (pazartesi = 1 … pazar = 0, JS uyumlu). */
  strengthWeekdays: number[]
}

/**
 * Bir gün için işaret.
 *
 * Sıralama önemli: tamamlanmış kayıt her şeyin üstünde, sonra yarım kalan,
 * sonra aktivite. "Kaçırıldı" yalnızca GEÇMİŞ günler için — gelecekteki bir
 * kuvvet gününü kırmızı göstermek kullanıcıyı henüz yapmadığı bir şey için
 * suçlamak olurdu.
 */
export function markFor(
  date: string,
  today: string,
  session: Session | undefined,
  activityCount: number,
  planlıKuvvet: boolean,
): DayMark {
  const yapildi = session?.exercises.some((e) => e.sets.some((s) => s.done)) ?? false
  if (session?.completedAt && yapildi) return 'done'
  if (yapildi) return 'partial'
  if (activityCount > 0) return 'activity'
  if (planlıKuvvet && date < today) return 'missed'
  return 'empty'
}

/** Ay görünümü: pazartesiyle başlayan 6×7 ızgara (bazı aylar 5 satır yeter, kırpıyoruz). */
export function buildMonth(input: BuildInput): CalendarDay[] {
  const first = monthStart(input.anchor)
  const ay = first.slice(0, 7)
  const start = weekStartMon(first)

  // 6 hafta her ayı kapsar; sonda tamamen boş kalan haftayı atıyoruz.
  const gunler: CalendarDay[] = []
  for (let i = 0; i < 42; i++) gunler.push(buildDay(shiftDate(start, i), ay, input))

  while (gunler.length > 35 && gunler.slice(35).every((g) => g.outside)) gunler.length = 35
  return gunler
}

/** Hafta görünümü: pazartesiden pazara 7 gün. */
export function buildWeek(input: BuildInput): CalendarDay[] {
  const start = weekStartMon(input.anchor)
  const ay = input.anchor.slice(0, 7)
  return Array.from({ length: 7 }, (_, i) => buildDay(shiftDate(start, i), ay, input))
}

function buildDay(date: string, ay: string, input: BuildInput): CalendarDay {
  const session = input.sessions.find((s) => s.date === date)
  const activityCount = input.activities.filter((a) => a.date === date).length
  // JS hafta günü (pazar = 0) — şablon da bu düzeni kullanıyor.
  const jsGun = new Date(`${date}T00:00:00Z`).getUTCDay()
  const planlı = input.strengthWeekdays.includes(jsGun)

  return {
    date,
    day: Number(date.slice(8, 10)),
    outside: date.slice(0, 7) !== ay,
    today: date === input.today,
    future: date > input.today,
    mark: markFor(date, input.today, session, activityCount, planlı),
    label: session ? (session.day ? `Antrenman ${session.day}` : null) : null,
    activityCount,
  }
}

export const MARK_LABEL: Record<DayMark, string> = {
  done: 'tamamlandı',
  partial: 'yarım kaldı',
  activity: 'aktivite',
  missed: 'kuvvet günüydü, kayıt yok',
  empty: 'kayıt yok',
}

/** Ay/hafta özeti — takvimin altında tek satır. */
export function summarize(days: CalendarDay[]): {
  done: number; partial: number; activity: number; missed: number
} {
  const say = (m: DayMark) => days.filter((d) => !d.outside && d.mark === m).length
  return { done: say('done'), partial: say('partial'), activity: say('activity'), missed: say('missed') }
}
